import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Button, Input, Table, Badge } from "reactstrap";
import axios from "axios";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";
import { NotificationManager } from "../../../components/common/react-notifications";
import { END_POINT } from "../../../constants/defaultValues";
import IntlMessages from "../../../helpers/IntlMessages";
import { injectIntl } from "react-intl";

const DEFAULT_TIERS = [
  { key: "bronze", name: "Đồng", minPoints: 1000, color: "#cd7f32" },
  { key: "silver", name: "Bạc", minPoints: 5000, color: "#c0c0c0" },
  { key: "gold", name: "Vàng", minPoints: 10000, color: "#ffd700" },
  { key: "diamond", name: "Kim Cương", minPoints: 20000, color: "#b9f2ff" },
];

const Setting = ({ intl }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [pointRate, setPointRate] = useState(1000);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    minPoints: "",
    color: "",
  });

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(`${END_POINT}/customer/settings`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (res.data && res.data.data) {
        if (res.data.data.customer_tiers) {
          setTiers(res.data.data.customer_tiers);
        }
        if (res.data.data.point_conversion_rate) {
          setPointRate(res.data.data.point_conversion_rate);
        }
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const tokenStr = localStorage.getItem("access_token");
      await axios.put(
        `${END_POINT}/customer/settings`,
        {
          customer_tiers: tiers,
          point_conversion_rate: pointRate,
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      NotificationManager.success(
        intl.formatMessage({ id: "settings.customer.alert.success" }),
        "",
        3000,
      );
    } catch (error) {
      console.error("Error saving settings:", error);
      NotificationManager.error(
        intl.formatMessage({ id: "settings.customer.alert.error" }),
        "",
        3000,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditForm({ ...tiers[index] });
  };

  const handleSaveEdit = () => {
    if (!editForm.name || editForm.minPoints === "") {
      NotificationManager.warning(
        intl.formatMessage({ id: "settings.customer.alert.missing-info" }),
        "",
        3000,
      );
      return;
    }
    const newTiers = [...tiers];
    newTiers[editingIndex] = {
      ...editForm,
      key: editForm.name.toLowerCase().replace(/\s+/g, "_"),
      minPoints: parseInt(editForm.minPoints) || 0,
    };
    // Sort by minPoints
    newTiers.sort((a, b) => a.minPoints - b.minPoints);
    setTiers(newTiers);
    setEditingIndex(null);
  };

  const handleAdd = () => {
    const maxPoints = Math.max(...tiers.map((t) => t.minPoints), 0);
    const newTier = {
      key: `tier_${Date.now()}`,
      name: intl.formatMessage({ id: "settings.customer.new-tier" }),
      minPoints: maxPoints + 500,
      color: "#808080",
    };
    setTiers([...tiers, newTier]);
    setEditingIndex(tiers.length);
    setEditForm(newTier);
  };

  const handleDelete = (index) => {
    if (tiers.length <= 1) {
      NotificationManager.warning(
        intl.formatMessage({ id: "settings.customer.alert.min-tiers" }),
        "",
        3000,
      );
      return;
    }
    const newTiers = tiers.filter((_, i) => i !== index);
    setTiers(newTiers);
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <CardBody className="text-center py-5">
          <div className="loading" />
          <p className="mt-2">
            <IntlMessages id="settings.customer.loading" />
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardBody>
        {/* Point Conversion Rate */}
        <Row className="mb-4">
          <Colxx xxs="12">
            <h6 className="mb-3">
              <i className="simple-icon-star mr-2"></i>
              <IntlMessages id="settings.customer.point-rule" />
            </h6>
          </Colxx>
          <Colxx xxs="12" sm="6" md="4">
            <div className="d-flex align-items-center">
              <span className="mr-2">
                <IntlMessages id="settings.customer.point-value" />
              </span>
              <Input
                type="number"
                value={pointRate}
                onChange={(e) => setPointRate(parseInt(e.target.value) || 1)}
                style={{ width: "100px" }}
                className="mr-2"
              />
              <span>
                <IntlMessages id="settings.customer.currency" />
              </span>
            </div>
          </Colxx>
        </Row>

        <Separator className="mb-4" />

        {/* Customer Tiers */}
        <Row>
          <Colxx
            xxs="12"
            className="d-flex justify-content-between align-items-center mb-3"
          >
            <h6 className="mb-0">
              <i className="simple-icon-badge mr-2"></i>
              <IntlMessages id="settings.customer.tier-title" />
            </h6>
            <Button color="success" size="sm" onClick={handleAdd}>
              <i className="simple-icon-plus mr-1"></i>
              <IntlMessages id="settings.customer.add-tier" />
            </Button>
          </Colxx>
          <Colxx xxs="12">
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>
                    <IntlMessages id="settings.customer.table.color" />
                  </th>
                  <th>
                    <IntlMessages id="settings.customer.table.name" />
                  </th>
                  <th style={{ width: "150px" }}>
                    <IntlMessages id="settings.customer.table.points" />
                  </th>
                  <th style={{ width: "120px" }}>
                    <IntlMessages id="settings.customer.table.actions" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier, index) => (
                  <tr key={tier.key}>
                    {editingIndex === index ? (
                      <>
                        <td>
                          <Input
                            type="color"
                            value={editForm.color}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                color: e.target.value,
                              })
                            }
                            style={{
                              width: "40px",
                              height: "30px",
                              padding: "2px",
                            }}
                          />
                        </td>
                        <td>
                          <Input
                            type="text"
                            value={editForm.name}
                            onChange={(e) =>
                              setEditForm({ ...editForm, name: e.target.value })
                            }
                            placeholder={intl.formatMessage({
                              id: "settings.customer.placeholder.name",
                            })}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            value={editForm.minPoints}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                minPoints: e.target.value,
                              })
                            }
                            placeholder={intl.formatMessage({
                              id: "settings.customer.placeholder.points",
                            })}
                          />
                        </td>
                        <td>
                          <Button
                            color="success"
                            size="xs"
                            className="mr-1"
                            onClick={handleSaveEdit}
                          >
                            <i className="simple-icon-check"></i>
                          </Button>
                          <Button
                            color="secondary"
                            size="xs"
                            onClick={() => setEditingIndex(null)}
                          >
                            <i className="simple-icon-close"></i>
                          </Button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          <Badge
                            style={{
                              backgroundColor: tier.color,
                              width: "30px",
                              height: "20px",
                              display: "inline-block",
                            }}
                          >
                            &nbsp;
                          </Badge>
                        </td>
                        <td>
                          <strong>{tier.name}</strong>
                        </td>
                        <td>
                          {tier.minPoints.toLocaleString("vi-VN")}{" "}
                          <IntlMessages id="settings.customer.points-suffix" />
                        </td>
                        <td>
                          <Button
                            color="primary"
                            size="xs"
                            className="mr-1"
                            onClick={() => handleEdit(index)}
                          >
                            <i className="simple-icon-pencil"></i>
                          </Button>
                          <Button
                            color="danger"
                            size="xs"
                            onClick={() => handleDelete(index)}
                          >
                            <i className="simple-icon-trash"></i>
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Colxx>
        </Row>

        <Separator className="my-4" />

        {/* Info */}
        <Row>
          <Colxx xxs="12">
            <div className="text-muted text-small">
              <i className="simple-icon-info mr-2"></i>
              <strong>
                <IntlMessages id="settings.customer.note-title" />
              </strong>{" "}
              <IntlMessages id="settings.customer.note-content" />
            </div>
          </Colxx>
        </Row>
        <div className="text-center">
          <Button
            className="mt-4"
            color="primary"
            size="sm"
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? (
              <IntlMessages id="settings.customer.saving" />
            ) : (
              <IntlMessages id="settings.customer.save-btn" />
            )}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default injectIntl(Setting);
